import { FC } from 'react';

import { resourceTypesWithColumns } from '@dbt-labs/dbt-dag';
import { TrustSignals } from '@dbt-labs/metadata-shared';

import { AccessLevel } from '@/__generated__/graphql';
import { PublicResourceIconLink } from '@/components/ResourceLinkList';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ResourceWithColumns } from '@/hooks/appliedColumns';
import { PublicModelResult } from '@/hooks/discovery/publicModels';
import { ResourceAppliedNode } from '@/hooks/discovery/resource';
import { useUrlProjectParams } from '@/hooks/params';
import { useRelationshipLinks } from '@/hooks/useRelationshipLinks';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { ColumnsView } from '@/pages/account/project/resource/details/components/Tabs/ColumnsView';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { ResourcePanelDetailsRelationships } from './ResourcePanelDetailsRelationship';
import { PanelDescription } from './ResourcePanels/Description';
import { GeneralResourceTable } from './ResourcePanels/GeneralResourceTable';
import { ResourcePanelTabs } from './ResourcePanelTabs';
import { ResourcePanelTitle } from './ResourcePanelTitle';

interface PanelDetailsProps {
  uniqueId: ParsedUniqueId;
  name: string | undefined;
  projectId: number | undefined;
  environmentId?: number | undefined;
  access: AccessLevel | undefined;
  isExternalModel: boolean;
  trustSignals: TrustSignals;
  resource: ResourceAppliedNode | PublicModelResult | undefined;
}

export const ResourcePanelDetails: FC<PanelDetailsProps> = ({
  uniqueId,
  name,
  access,
  projectId,
  environmentId = 0,
  isExternalModel,
  trustSignals,
  resource,
}) => {
  const { accountId, dbtCloudProjects, hasProjectAccess } = useDbtCloudAccount();
  const projectScope = useUrlProjectParams();
  const project = dbtCloudProjects?.find((p) => p.id === projectId);

  // only want referencedByProjects info
  const relationshipLinks = useRelationshipLinks({
    uniqueId,
    projectScope,
    onlyReferencedByProjects: true,
  });
  const links = relationshipLinks?.links as PublicResourceIconLink[];
  const getTargetEnvironmentType = useTargetEnvironmentType();

  if (!accountId || !projectId) return null;

  const dbtCorePackageName = uniqueId.packageName;
  const panelProps = {
    accountId,
    projectId,
    environmentId,
    uniqueId,
    projectName: project?.name || dbtCorePackageName,
  };

  return (
    <>
      <ResourcePanelTitle
        access={access}
        className="p-4"
        environmentType={getTargetEnvironmentType(environmentId)}
        name={name}
        projectId={projectId}
        trustSignals={trustSignals}
        uniqueId={uniqueId}
      />
      <ResourcePanelTabs
        showColumns={resourceTypesWithColumns.includes(uniqueId.resourceType)}
        isColumnsTabEnabled={hasProjectAccess(projectId)}
        showRelationships={access === 'public' && !isExternalModel}
      >
        {(tabType) => {
          return (
            <>
              {tabType === 'general' && (
                <>
                  <GeneralResourceTable
                    {...panelProps}
                    className="m-6"
                    isExternalModel={isExternalModel}
                    compact
                    children={(panelProps: any) => <PanelDescription {...panelProps} />}
                    showAutoExposureActions={true}
                  />
                </>
              )}
              {tabType === 'columns' && (
                <ColumnsView
                  resource={resource as ResourceWithColumns}
                  shouldShowLineage={false}
                  className="px-4"
                />
              )}
              {tabType === 'relationships' && (
                <ResourcePanelDetailsRelationships links={links} />
              )}
            </>
          );
        }}
      </ResourcePanelTabs>
    </>
  );
};
